import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Settings, ChevronRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const TopicsView = ({ user, onLogout }) => {
  const { specialty, submodule } = useParams();
  const decodedSpecialty = decodeURIComponent(specialty);
  const decodedSubmodule = submodule ? decodeURIComponent(submodule) : null;
  const navigate = useNavigate();
  
  const effectiveSpecialty = decodedSubmodule 
    ? `${decodedSpecialty} - ${decodedSubmodule}` 
    : decodedSpecialty;
  
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [passedQuizzes, setPassedQuizzes] = useState([]);
  const [passCounts, setPassCounts] = useState({});

  useEffect(() => {
    fetchQuizzesBySpecialty();
    fetchProgress();
    fetchPassCounts();
  }, [specialty, submodule]);

  const fetchQuizzesBySpecialty = async () => {
    try {
      const response = await apiClient.get(`/quizzes?specialty=${encodeURIComponent(effectiveSpecialty)}`);
      setQuizzes(response.data);
    } catch (error) {
      toast.error('Error al cargar los temas');
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPassCounts = async () => {
    try {
      const response = await apiClient.get('/progress/pass-counts');
      setPassCounts(response.data.pass_counts || {});
    } catch (error) {
      console.error('Error fetching pass counts:', error);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await apiClient.get('/progress/quizzes');
      setPassedQuizzes(response.data.passed || []);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const isPassed = (quizId) => passedQuizzes.includes(quizId);

  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleTopicClick = (quizId) => {
    setActiveItem(quizId);
    playPencilSound();
    setTimeout(() => {
      navigate(`/quiz/${quizId}`);
    }, 150);
  };

  const handleBack = () => {
    if (decodedSubmodule) {
      navigate(`/submodules/${encodeURIComponent(decodedSpecialty)}`);
    } else {
      navigate('/cuestionarios');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Volver</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-black mb-6 text-center">
            {decodedSubmodule ? `${decodedSpecialty} - ${decodedSubmodule}` : decodedSpecialty}
          </h1>

          {quizzes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No hay temas disponibles aún</p>
              <button
                onClick={() => navigate('/cuestionarios')}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Volver a Cuestionarios
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {quizzes.map((quiz, index) => {
                const isHovered = hoveredItem === quiz.id;
                const isActive = activeItem === quiz.id;
                const passed = isPassed(quiz.id);
                const passCount = passCounts[quiz.id] || 0;
                
                return (
                  <div key={quiz.id} className="relative">
                    {/* Pass count badge - top right */}
                    {passCount > 0 && (
                      <div className="absolute -top-2 -right-2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-blue-500">
                        <span className="text-xs font-bold text-blue-500">{passCount}</span>
                      </div>
                    )}
                    
                    <button
                      onClick={() => handleTopicClick(quiz.id)}
                      onMouseEnter={() => setHoveredItem(quiz.id)}
                      onMouseLeave={() => setHoveredItem(null)}
                      data-testid={`topic-card-${quiz.topic.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`w-full border rounded-xl p-4 transition-all duration-200 text-left flex items-start space-x-3 ${
                        passed
                          ? 'bg-gray-100 border-gray-200'
                          : isHovered || isActive
                            ? 'bg-white border-black shadow-md'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {/* Circle indicator */}
                      <div className="flex-shrink-0 mt-1">
                        <span 
                          className={`relative flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-200 ${
                            passed
                              ? 'border-gray-400'
                              : isHovered || isActive ? 'border-black' : 'border-gray-400'
                          }`}
                        >
                          <span 
                            className={`absolute w-3 h-3 rounded-full transition-all duration-200 ${
                              passed
                                ? 'bg-gray-400 scale-100 opacity-100'
                                : isHovered || isActive ? 'bg-black scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          ></span>
                        </span>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-sm sm:text-base font-semibold leading-snug transition-colors duration-200 ${
                          passed
                            ? 'text-gray-500'
                            : isHovered || isActive ? 'text-black' : 'text-gray-800'
                        }`}>
                          {quiz.topic}
                        </h3>
                        <p className={`text-xs sm:text-sm mt-1 ${passed ? 'text-gray-400' : 'text-gray-500'}`}>
                          {quiz.questions.length} preguntas
                        </p>
                      </div>
                      
                      {/* Checkmark for passed or Arrow */}
                    <div className="flex-shrink-0 self-center">
                      {passed ? (
                        <Check 
                          size={20} 
                          className="text-green-500"
                          strokeWidth={3}
                        />
                      ) : (
                        <ChevronRight 
                          size={20} 
                          className={`transition-all duration-200 ${
                            isHovered || isActive ? 'text-black translate-x-1' : 'text-gray-400'
                          }`}
                        />
                      )}
                    </div>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopicsView;
